import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import { formatAmount } from "../../utils/hooks/dataHandler";
import MainComputationCalculator from "../../builders/calculator/mainComputation";
import { getPrecedingApportionmentBasis } from "./utils/precedingCalculation";
import {
  getManualTaxPreviouslyAssessedRows,
  isAutoloadTaxPreviouslyAssessedRow
} from "./Pdf/shared";

export const apportionAmount = (amount, context, amountKey) => {
  const numericAmount = parseFloat(amount) || 0;
  if (!context?.totalDays) return numericAmount;
  const precedingBasis = getPrecedingApportionmentBasis(context);

  if (amountKey === "preceding_amount") {
    return Math.round(numericAmount * precedingBasis);
  }

  const precedingAmount = Math.round(numericAmount * precedingBasis);
  return numericAmount - precedingAmount;
};

const getNumericValue = (row, amountKey, context) => {
  if (Object.prototype.hasOwnProperty.call(row || {}, amountKey)) {
    return parseFloat(row?.[amountKey]) || 0;
  }

  return apportionAmount(row?.amount, context, amountKey);
};

const getYAKeyFromAmountKey = (amountKey) =>
  amountKey === "preceding_amount" ? "preceding" : "current";

export const getYAConfigValue = (config, amountKey) => {
  const yaKey = getYAKeyFromAmountKey(amountKey);

  if (
    config &&
    typeof config === "object" &&
    !Array.isArray(config) &&
    Object.prototype.hasOwnProperty.call(config, yaKey)
  ) {
    return config[yaKey];
  }

  return config;
};

export const calTaxAdjustmentsByKey = (rows = [], amountKey, context) =>
  rows.reduce((sum, row) => {
    if (row.row_id === "ca-summary-00") {
      return sum + Math.round(getNumericValue(row, amountKey, context));
    }

    const amount = Math.abs(
      Math.round(getNumericValue(row, amountKey, context))
    );
    return row.taxAdjustment === "Deduct" ? sum - amount : sum + amount;
  }, 0);

const sumRowsByKey = (rows = [], amountKey, context, shouldDeduct = false) =>
  rows.reduce((sum, row) => {
    const amount = Math.round(getNumericValue(row, amountKey, context));
    return shouldDeduct ? sum - amount : sum + amount;
  }, 0);

export const buildYABaseComputation = ({
  amountKey,
  context,
  netProfitBeforeTax,
  referenceData,
  isInvestmentCompany = false,
}) => {
  const splitNetProfitBeforeTax = isInvestmentCompany
    ? 0
    : apportionAmount(netProfitBeforeTax, context, amountKey);
  const taxAdjustments = calTaxAdjustmentsByKey(
    referenceData.taxAdjustments,
    amountKey,
    context
  );
  const separateSourcedIncome = calTaxAdjustmentsByKey(
    referenceData.taxAdjustmentSeparateSourcedIncome,
    amountKey,
    context
  );
  const balancingCharge = calTaxAdjustmentsByKey(
    referenceData.taxAdjustmentBalancingCharge,
    amountKey,
    context
  );
  const furtherDeductionS14n = calTaxAdjustmentsByKey(
    referenceData.furtherDeductionS14n,
    amountKey,
    context
  );
  const furtherDeductionOther = calTaxAdjustmentsByKey(
    referenceData.furtherDeductionOther,
    amountKey,
    context
  );
  const currentYearCa = calTaxAdjustmentsByKey(
    referenceData.currentYearCapitalAllowances,
    amountKey,
    context
  );
  const otherIncome = sumRowsByKey(
    referenceData.otherSourceIncome,
    amountKey,
    context
  );
  const currentYearDonation = calTaxAdjustmentsByKey(
    referenceData.currentYearDonations,
    amountKey,
    context
  );
  const currentYearInvestmentLoss = calTaxAdjustmentsByKey(
    referenceData.currentYearInvestmentLosses,
    amountKey,
    context
  );

  const adjustedProfitAfterTaxAdjustments =
    splitNetProfitBeforeTax +
    taxAdjustments +
    separateSourcedIncome +
    balancingCharge +
    furtherDeductionS14n +
    furtherDeductionOther;
  const currentYearLosses =
    adjustedProfitAfterTaxAdjustments < 0
      ? Math.abs(adjustedProfitAfterTaxAdjustments)
      : 0;

  let utilisationAvailable =
    adjustedProfitAfterTaxAdjustments < 0
      ? otherIncome
      : adjustedProfitAfterTaxAdjustments + otherIncome;

  if (utilisationAvailable < 0) utilisationAvailable = 0;
  if (currentYearCa > 0) utilisationAvailable += currentYearCa;
  if (currentYearDonation > 0) utilisationAvailable += currentYearDonation;

  return {
    splitNetProfitBeforeTax,
    taxAdjustments,
    separateSourcedIncome,
    balancingCharge,
    furtherDeductionS14n,
    furtherDeductionOther,
    currentYearCa,
    otherIncome,
    currentYearDonation,
    currentYearInvestmentLoss,
    adjustedProfitAfterTaxAdjustments,
    currentYearLosses,
    utilisationAvailable
  };
};

const getActiveRows = (rows = []) => rows.filter((row) => !row?.isForfeited);

const calUnutilisedByKey = (
  rows = [],
  amountKey,
  context,
  useAutoloadAdjustment = false,
) =>
  getActiveRows(rows).reduce((sum, row) => {
    const amount = Math.abs(getNumericValue(row, amountKey, context));
    if (
      useAutoloadAdjustment &&
      row?.status === "autoload" &&
      row?.taxAdjustment === "Added Back"
    ) {
      return sum + amount;
    }

    return sum - amount;
  }, 0);

const calTaxPreviouslyAssessedByKey = (rows = [], amountKey, context) =>
  rows.reduce((sum, row) => {
    if (!isAutoloadTaxPreviouslyAssessedRow(row)) return sum;

    const amount = Math.abs(getNumericValue(row, amountKey, context));
    if (!row.taxAdjustment) return sum - amount;
    return row.taxAdjustment === "Deduct" ? sum - amount : sum + amount;
  }, 0);

const buildSplitRows = (rows = [], amountKey, context) =>
  rows.map((row) => ({
    ...row,
    amount: getNumericValue(row, amountKey, context)
  }));

const calculateExemption = (chargeableIncome, isStartup) => {
  if (chargeableIncome <= 0) {
    return {
      firstExemptAmount: null,
      secondExemptAmount: null,
      totalExemptedAmount: 0,
      chargebleIncomeAfterExemptAmount: null
    };
  }

  const firstExemptAmount = isStartup
    ? Math.round(Math.min(chargeableIncome, 100000) * 0.75)
    : Math.round(Math.min(chargeableIncome, 10000) * 0.75);
  const secondExemptAmount = Math.round(
    isStartup
      ? Math.min(Math.max(chargeableIncome - 100000, 0), 100000) * 0.5
      : Math.min(Math.max(chargeableIncome - 10000, 0), 190000) * 0.5
  );
  const totalExemptedAmount = firstExemptAmount + secondExemptAmount;

  return {
    firstExemptAmount,
    secondExemptAmount,
    totalExemptedAmount,
    chargebleIncomeAfterExemptAmount: Math.round(
      chargeableIncome - totalExemptedAmount
    )
  };
};

const applyCorporateRebate = ({
  postDeductions,
  corporateIncomeTaxRebate,
  currentYearRebate
}) => {
  let rebate = 0;

  if (
    postDeductions > 0 &&
    corporateIncomeTaxRebate?.taxableRate &&
    corporateIncomeTaxRebate?.cappedAmount
  ) {
    rebate =
      -Math.round(
        Math.min(
          postDeductions * (corporateIncomeTaxRebate.taxableRate / 100),
          Number(corporateIncomeTaxRebate.cappedAmount)
        ) * 100
      ) / 100;
  }

  if (corporateIncomeTaxRebate?.isRebate) {
    if (Math.abs(rebate) > Math.abs(currentYearRebate?.amount || 0)) {
      rebate = -(Math.abs(rebate) - Math.abs(currentYearRebate?.amount || 0));
    } else {
      rebate = 0;
    }
  }

  return rebate;
};

const formatCarryForwardLabel = (label, value) => {
  if (label.includes("After Capital Allowances") && value < 0) {
    return "Unutilised Capital Allowances c/f";
  }
  if (label.includes("Losses") && value < 0) return "Unutilised Losses c/f";
  if (label.includes("Donations") && value < 0) {
    return "Unutilised Donations c/f";
  }
  if (label.includes("Before Capital Allowances") && value < 0) {
    return "Current Year Losses";
  }
  return label;
};

const hasSummaryValue = (value) =>
  value !== null && value !== undefined && value !== "";

export const buildYASummary = ({
  amountKey,
  company,
  context,
  corporateIncomeTaxRebate,
  currency,
  currencyRate,
  currentYearRebate,
  isCurrentYA,
  isInvestmentCompany,
  isStartup,
  netProfitBeforeTax,
  referenceData,
  taxPreviouslyAssessed,
  unutilisedCapitalAllowances,
  unutilisedDonations,
  unutilisedLosses,
  yearLossesBroughtForward = 0,
  groupReliefClaimSgd = 0
}) => {
  const yaIsStartup = Boolean(getYAConfigValue(isStartup, amountKey));
  const yaCorporateIncomeTaxRebate =
    getYAConfigValue(corporateIncomeTaxRebate, amountKey) || {};
  const yaCurrentYearRebate =
    getYAConfigValue(currentYearRebate, amountKey) || {};
  const {
    adjustedProfitAfterTaxAdjustments,
    currentYearCa: CURRENT_CA,
    currentYearDonation: CURRENT_DONATION,
    currentYearInvestmentLoss: CURRENT_YEAR_INVESTMENT_LOSS,
    otherIncome: OTHER_INCOME,
    splitNetProfitBeforeTax
  } = buildYABaseComputation({
    amountKey,
    context,
    netProfitBeforeTax,
    referenceData,
    isInvestmentCompany,
  });
  const UN_CA = calUnutilisedByKey(
    unutilisedCapitalAllowances,
    amountKey,
    context
  );
  const UN_LOSS = calUnutilisedByKey(
    unutilisedLosses,
    amountKey,
    context,
    true,
  );
  const UN_DONATION = calUnutilisedByKey(
    unutilisedDonations,
    amountKey,
    context
  );
  const TAX_PREV_ASSESS = calTaxPreviouslyAssessedByKey(
    taxPreviouslyAssessed,
    amountKey,
    context
  );

  let currentYearLosses = null;
  let unutilisedCAcf = null;
  let unutilisedLosscf = null;
  let unutilisedDonationcf = null;
  const yearLossesBroughtForwardAmount = Math.abs(
    parseFloat(yearLossesBroughtForward) || 0
  );
  const hasCurrentYearLosses = adjustedProfitAfterTaxAdjustments < 0;
  const yearLossesForUnutilisedLosses = isCurrentYA
    ? (hasCurrentYearLosses ? Math.abs(adjustedProfitAfterTaxAdjustments) : 0) +
      yearLossesBroughtForwardAmount
    : 0;

  let adjustedProfitAfterCapitalAllowances = 0;
  let adjustedProfitAfterLosses = 0;
  let adjustedProfitAfterDonations = 0;

  if (hasCurrentYearLosses) {
    currentYearLosses = Math.abs(adjustedProfitAfterTaxAdjustments);
    adjustedProfitAfterCapitalAllowances =
      OTHER_INCOME + CURRENT_CA - Math.abs(UN_CA);

    if (adjustedProfitAfterCapitalAllowances < 0) {
      unutilisedCAcf = adjustedProfitAfterCapitalAllowances;
    }

    adjustedProfitAfterLosses =
      UN_LOSS - yearLossesForUnutilisedLosses;
    if (adjustedProfitAfterLosses < 0) {
      unutilisedLosscf = adjustedProfitAfterLosses;
    }

    adjustedProfitAfterDonations = CURRENT_DONATION - Math.abs(UN_DONATION);
    if (adjustedProfitAfterDonations < 0) {
      unutilisedDonationcf = adjustedProfitAfterDonations;
    }
  } else {
    adjustedProfitAfterCapitalAllowances =
      adjustedProfitAfterTaxAdjustments +
      OTHER_INCOME +
      CURRENT_CA -
      Math.abs(UN_CA);

    adjustedProfitAfterLosses =
      UN_LOSS - yearLossesForUnutilisedLosses;
    if (adjustedProfitAfterCapitalAllowances < 0) {
      unutilisedCAcf = adjustedProfitAfterCapitalAllowances;
    } else {
      adjustedProfitAfterLosses =
        adjustedProfitAfterCapitalAllowances + adjustedProfitAfterLosses;
    }

    adjustedProfitAfterDonations = CURRENT_DONATION - Math.abs(UN_DONATION);
    if (adjustedProfitAfterLosses < 0) {
      unutilisedLosscf = adjustedProfitAfterLosses;
      if (adjustedProfitAfterDonations < 0) {
        unutilisedDonationcf = adjustedProfitAfterDonations;
      }
    } else {
      adjustedProfitAfterDonations =
        adjustedProfitAfterLosses + adjustedProfitAfterDonations;
    }

    if (adjustedProfitAfterDonations < 0) {
      unutilisedDonationcf = adjustedProfitAfterDonations;
    }
  }

  let chargebleIncomeBeforeExemptAmount = 0;
  if (!currentYearLosses) {
    chargebleIncomeBeforeExemptAmount = adjustedProfitAfterDonations;
  }

  if (isInvestmentCompany) {
    chargebleIncomeBeforeExemptAmount =
      adjustedProfitAfterCapitalAllowances +
      CURRENT_DONATION +
      CURRENT_YEAR_INVESTMENT_LOSS;
  }

  if (chargebleIncomeBeforeExemptAmount > 0) {
    if (currency !== "SGD") {
      chargebleIncomeBeforeExemptAmount =
        MainComputationCalculator.convertChargeableIncomeToSgd(
          chargebleIncomeBeforeExemptAmount,
          currencyRate,
          [
            ...buildSplitRows(unutilisedCapitalAllowances, amountKey, context),
            ...buildSplitRows(unutilisedLosses, amountKey, context),
            ...buildSplitRows(unutilisedDonations, amountKey, context)
          ]
        );
    }

    chargebleIncomeBeforeExemptAmount = Math.round(
      chargebleIncomeBeforeExemptAmount
    );
  }

  // Group relief is denominated in SGD and applies only to this YA after the
  // existing own-company utilisation and currency conversion have completed.
  if (groupReliefClaimSgd > 0) chargebleIncomeBeforeExemptAmount = Math.max(0, chargebleIncomeBeforeExemptAmount - groupReliefClaimSgd);
  const exemption = calculateExemption(
    chargebleIncomeBeforeExemptAmount,
    yaIsStartup
  );
  const taxableIncome =
    chargebleIncomeBeforeExemptAmount > 0 &&
    exemption.chargebleIncomeAfterExemptAmount > 0
      ? (exemption.chargebleIncomeAfterExemptAmount * 0.17).toFixed(2)
      : 0;

  const prevTaxPreviouslyAssessed = Math.abs(
    getManualTaxPreviouslyAssessedRows(taxPreviouslyAssessed, (row) =>
      getNumericValue(row, amountKey, context)
    ).reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0)
  );
  const totalTaxPreviouslyAssessed = TAX_PREV_ASSESS;
  const postDeductions =
    Number(taxableIncome) + Number(totalTaxPreviouslyAssessed);
  const corporateRebate = applyCorporateRebate({
    postDeductions,
    corporateIncomeTaxRebate: yaCorporateIncomeTaxRebate,
    currentYearRebate: yaCurrentYearRebate
  });
  const taxPayableBeforeEci =
    chargebleIncomeBeforeExemptAmount <= 0
      ? 0
      : Number(postDeductions) + Number(corporateRebate);
  const netTaxPayable =
    Number(postDeductions) +
    Number(corporateRebate) -
    Number(prevTaxPreviouslyAssessed);

  const rawSteps = isInvestmentCompany
    ? [
        {
          label: "Net Investment Income",
          value: adjustedProfitAfterTaxAdjustments,
          tooltip:
            "Total investment income after adjustments for this YA portion."
        },
        {
          label: "Current Year Capital Allowances",
          value: CURRENT_CA,
          tooltip:
            "Current year capital allowances tagged from Savy schedules."
        },
        {
          label: "Current Year Losses",
          value: CURRENT_YEAR_INVESTMENT_LOSS,
          tooltip:
            "Current year investment losses tagged from Savy schedules."
        },
        {
          label: "Current Year Donations",
          value: CURRENT_DONATION,
          tooltip: "Current year donations tagged from Savy schedules."
        },
        {
          label: "Chargeable Income (Before Exempt)",
          value: chargebleIncomeBeforeExemptAmount,
          tooltip:
            "Chargeable income for this YA before applying its own exemption."
        }
      ]
    : [
        {
          label: "Adjusted Profit Before Capital Allowances",
          value: adjustedProfitAfterTaxAdjustments,
          tooltip:
            "Net profit after tax adjustments for this YA, before capital allowances."
        },
        {
          label: "Adjusted Profit After Capital Allowances",
          value: hasSummaryValue(unutilisedCAcf)
            ? unutilisedCAcf
            : adjustedProfitAfterCapitalAllowances,
          carryForwardLabel: "Unutilised Capital Allowances c/f",
          isCarryForward:
            hasCurrentYearLosses || hasSummaryValue(unutilisedCAcf),
          tooltip: "Profit after capital allowances for this YA."
        },
        {
          label: "Adjusted Profit After Unutilised Losses",
          value: hasSummaryValue(unutilisedLosscf)
            ? unutilisedLosscf
            : adjustedProfitAfterLosses,
          carryForwardLabel: "Unutilised Losses c/f",
          isCarryForward: hasSummaryValue(unutilisedLosscf),
          tooltip: "Remaining profit after unutilised losses for this YA."
        },
        {
          label: "Adjusted Profit After Unutilised Donations",
          value: hasSummaryValue(unutilisedDonationcf)
            ? unutilisedDonationcf
            : adjustedProfitAfterDonations,
          carryForwardLabel: "Unutilised Donations c/f",
          isCarryForward: hasSummaryValue(unutilisedDonationcf),
          tooltip: "Remaining profit after unutilised donations for this YA."
        },
        {
          label: "Chargeable Income (Before Exempt)",
          value: chargebleIncomeBeforeExemptAmount,
          tooltip:
            "Chargeable income for this YA before applying its own exemption."
        }
      ];

  const toSgd = (amount) => currency === "SGD" ? amount : MainComputationCalculator.convertChargeableIncomeToSgd(amount, currencyRate, []);
  const available = (current, remaining) => Math.floor(Math.max(0, toSgd(Math.min(Math.max(0, current), Math.max(0, remaining)))));
  const outputAmount = (amount) => Math.round(toSgd(Number(amount || 0)));
  return {
    groupRelief: {
      assessableIncome: Math.max(0, Math.round(toSgd(isInvestmentCompany ? adjustedProfitAfterCapitalAllowances : adjustedProfitAfterLosses))),
      utilisation: {
        currentYearCAAvailable: isInvestmentCompany ? 0 : available(-CURRENT_CA, -(unutilisedCAcf || 0)),
        currentYearLossesAvailable: isInvestmentCompany ? 0 : available(currentYearLosses || 0, -(unutilisedLosscf || 0)),
        currentYearDonationsAvailable: available(-CURRENT_DONATION, isInvestmentCompany ? -(adjustedProfitAfterCapitalAllowances + CURRENT_DONATION) : -(unutilisedDonationcf || 0)),
      },
    },
    groupReliefDetailedOutput: {
      adjustedProfit: outputAmount(
        isInvestmentCompany
          ? adjustedProfitAfterCapitalAllowances
          : Math.max(0, adjustedProfitAfterTaxAdjustments)
      ),
      priorYearCA: isInvestmentCompany ? 0 : outputAmount(-Math.abs(UN_CA)),
      currentYearCA: isInvestmentCompany ? 0 : outputAmount(CURRENT_CA),
      otherSourcedIncome: isInvestmentCompany ? 0 : outputAmount(OTHER_INCOME),
      priorYearLosses: isInvestmentCompany
        ? 0
        : outputAmount(
            UN_LOSS - yearLossesBroughtForwardAmount
          ),
      currentYearLosses: isInvestmentCompany
        ? 0
        : outputAmount(Math.min(0, adjustedProfitAfterTaxAdjustments)),
      priorYearDonations: isInvestmentCompany
        ? 0
        : outputAmount(-Math.abs(UN_DONATION)),
      currentYearDonations: outputAmount(CURRENT_DONATION),
    },
    splitNetProfitBeforeTax,
    currentYearLosses,
    yearLossesBroughtForward: yearLossesBroughtForwardAmount,
    totalYearLossesForUnutilisedLosses: yearLossesForUnutilisedLosses,
    unutilisedCAcf,
    unutilisedLosscf,
    unutilisedDonationcf,
    chargebleIncomeBeforeExemptAmount,
    ...exemption,
    taxableIncome,
    postDeductions,
    corporateRebate,
    taxPayableBeforeEci,
    netTaxPayable,
    steps: rawSteps.map((step) => ({
      ...step,
      label: isInvestmentCompany
        ? step.label
        : step.isCarryForward && step.carryForwardLabel
          ? step.carryForwardLabel
          : formatCarryForwardLabel(step.label, step.value)
    }))
  };
};

const SummaryAmount = ({ value, strong = false, color }) => (
  <Typography
    variant={strong ? "subtitle1" : "subtitle2"}
    fontWeight={strong ? 700 : 600}
    color={color}
  >
    {formatAmount(value || 0, false)}
  </Typography>
);

const AmountCell = ({ loading, value, strong = false, color }) => (
  <TableCell align='right'>
    {loading ? (
      <Skeleton width={96} height={24} sx={{ ml: "auto" }} />
    ) : (
      <SummaryAmount value={value} strong={strong} color={color} />
    )}
  </TableCell>
);

const LabelCell = ({ label, tooltip, emphasized = false }) => (
  <TableCell>
    {tooltip ? (
      <Tooltip title={tooltip} arrow>
        <Typography
          variant='body2'
          sx={{ cursor: "help", fontWeight: emphasized ? 700 : 500 }}
        >
          {label}
        </Typography>
      </Tooltip>
    ) : (
      <Typography variant='body2' sx={{ fontWeight: emphasized ? 700 : 500 }}>
        {label}
      </Typography>
    )}
  </TableCell>
);

const SummaryRow = ({
  color,
  current,
  currentColor,
  emphasized = false,
  label,
  loading,
  preceding,
  precedingColor,
  rowIndex,
  tooltip
}) => (
  <TableRow
    sx={(theme) => ({
      "& > .MuiTableCell-root": {
        borderColor: alpha(theme.palette.divider, 0.16),
        py: 1.25,
        bgcolor:
          rowIndex % 2 === 0
            ? alpha(theme.palette.grey[500], 0.035)
            : "transparent"
      },
      "&:hover > .MuiTableCell-root": {
        bgcolor: alpha(theme.palette.primary.main, 0.08)
      }
    })}
  >
    <LabelCell label={label} tooltip={tooltip} emphasized={emphasized} />
    <AmountCell
      loading={loading}
      value={preceding}
      strong={emphasized}
      color={precedingColor || color}
    />
    <AmountCell
      loading={loading}
      value={current}
      strong={emphasized}
      color={currentColor || color}
    />
  </TableRow>
);

const SectionHeaderRow = ({ children }) => (
  <TableRow>
    <TableCell
      colSpan={3}
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        color: "text.secondary",
        fontWeight: 700,
        py: 1,
        textTransform: "uppercase",
        fontSize: 12,
        letterSpacing: 0
      })}
    >
      {children}
    </TableCell>
  </TableRow>
);

const buildTableRows = (
  precedingSummary,
  currentSummary,
  isInvestmentCompany
) => {
  const stepCount = Math.max(
    precedingSummary.steps.length,
    currentSummary.steps.length
  );
  const stepRows = Array.from({ length: stepCount }, (_, index) => {
    const precedingStep = precedingSummary.steps[index] || {};
    const currentStep = currentSummary.steps[index] || {};

    return {
      key: `step-${index}`,
      label:
        precedingStep.label ||
        currentStep.label ||
        `Computation Step ${index + 1}`,
      preceding: precedingStep.value,
      current: currentStep.value,
      tooltip: precedingStep.tooltip || currentStep.tooltip
    };
  });

  return [
    {
      key: "net-profit",
      label: isInvestmentCompany
        ? "Net Investment Income apportioned to YA"
        : "Net Profit / (Loss) before Tax apportioned to YA",
      preceding: precedingSummary.splitNetProfitBeforeTax,
      current: currentSummary.splitNetProfitBeforeTax,
      emphasized: true
    },
    ...stepRows,
    { key: "exemption-header", type: "section", label: "Tax Exemption" },
    {
      key: "chargeable-before-exempt",
      label: "Chargeable Income (Before Exempt)",
      preceding: precedingSummary.chargebleIncomeBeforeExemptAmount,
      current: currentSummary.chargebleIncomeBeforeExemptAmount
    },
    {
      key: "less-exempt",
      label: "Less: Exempt Amount",
      preceding: -Math.abs(precedingSummary.totalExemptedAmount),
      current: -Math.abs(currentSummary.totalExemptedAmount),
      tooltip: "Tax exemption is calculated independently for each YA."
    },
    {
      key: "chargeable-after-exempt",
      label: "Chargeable Income (After Exempt)",
      preceding: precedingSummary.chargebleIncomeAfterExemptAmount,
      current: currentSummary.chargebleIncomeAfterExemptAmount,
      emphasized: true
    },
    { key: "tax-header", type: "section", label: "Tax Calculations" },
    {
      key: "tax-payable",
      label: "Tax Payable at 17%",
      preceding: precedingSummary.taxableIncome,
      current: currentSummary.taxableIncome,
      color: "primary.main"
    },
    {
      key: "post-deductions",
      label: "Tax Payable (Post-Deductions)",
      preceding: precedingSummary.postDeductions,
      current: currentSummary.postDeductions,
      color: "primary.main"
    },
    {
      key: "net-tax",
      label: "Net Tax Payable / (Refunded)",
      preceding: precedingSummary.netTaxPayable,
      current: currentSummary.netTaxPayable,
      emphasized: true,
      getColor: (value) => (value >= 0 ? "error.main" : "success.main")
    }
  ];
};

const CrossYACalculationSummary = ({
  company,
  corporateIncomeTaxRebate,
  crossYAApportionmentContext,
  currency,
  currencyRate,
  currentYearRebate,
  isInvestmentCompany,
  isStartup,
  loading,
  netProfitBeforeTax,
  referenceData,
  taxPreviouslyAssessed,
  unutilisedCapitalAllowances,
  unutilisedDonations,
  unutilisedLosses
}) => {
  const currentYA = Number(company?.yearOfAssessment);
  const precedingYA = currentYA - 1;
  const currentDays =
    crossYAApportionmentContext.totalDays -
    crossYAApportionmentContext.precedingDays;
  const commonProps = {
    company,
    context: crossYAApportionmentContext,
    corporateIncomeTaxRebate,
    currency,
    currencyRate,
    currentYearRebate,
    isInvestmentCompany,
    isStartup,
    netProfitBeforeTax,
    referenceData,
    taxPreviouslyAssessed,
    unutilisedCapitalAllowances,
    unutilisedDonations,
    unutilisedLosses
  };
  const precedingSummary = buildYASummary({
    ...commonProps,
    amountKey: "preceding_amount",
    isCurrentYA: false
  });
  const currentSummary = buildYASummary({
    ...commonProps,
    amountKey: "current_amount",
    isCurrentYA: true,
    yearLossesBroughtForward: precedingSummary.currentYearLosses
  });
  const tableRows = buildTableRows(
    precedingSummary,
    currentSummary,
    isInvestmentCompany
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper"
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 3 }}>
        <CalculateIcon color='primary' />
        <Box>
          <Typography variant='h6' fontWeight={600}>
            Computed Summary by YA
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Cross YA basis period split into YA {precedingYA} and YA {currentYA}
            .
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2}>
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
          <Chip
            size='small'
            label={`Preceding YA ${precedingYA}: ${crossYAApportionmentContext.precedingDays} days`}
            color='primary'
            variant='outlined'
          />
          <Chip
            size='small'
            label={`Current YA ${currentYA}: ${currentDays} days`}
            color='primary'
            variant='outlined'
          />
        </Stack>

        <TableContainer
          component={Paper}
          variant='outlined'
          sx={{
            borderRadius: 2,
            overflowX: "auto"
          }}
        >
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={(theme) => ({
                    bgcolor: alpha(theme.palette.grey[500], 0.12),
                    fontWeight: 700,
                    minWidth: 280
                  })}
                >
                  Sections
                </TableCell>
                <TableCell
                  align='right'
                  sx={(theme) => ({
                    bgcolor: alpha(theme.palette.grey[500], 0.12),
                    fontWeight: 700,
                    minWidth: 180
                  })}
                >
                  Preceding YA {precedingYA}
                </TableCell>
                <TableCell
                  align='right'
                  sx={(theme) => ({
                    bgcolor: alpha(theme.palette.grey[500], 0.12),
                    fontWeight: 700,
                    minWidth: 180
                  })}
                >
                  Current YA {currentYA}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row, index) =>
                row.type === "section" ? (
                  <SectionHeaderRow key={row.key}>{row.label}</SectionHeaderRow>
                ) : (
                  <SummaryRow
                    key={row.key}
                    label={row.label}
                    tooltip={row.tooltip}
                    preceding={row.preceding}
                    current={row.current}
                    emphasized={row.emphasized}
                    loading={loading}
                    color={row.color}
                    rowIndex={index}
                    precedingColor={row.getColor?.(row.preceding)}
                    currentColor={row.getColor?.(row.current)}
                  />
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
};

export default CrossYACalculationSummary;
